import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Gauge, Plus, ListChecks, AlertTriangle, Sparkles, ScrollText, Users, UserCog,
  Landmark, Handshake, DollarSign, Gift, Globe, Megaphone, BookCheck, Brain, Radio, MessageSquare } from "lucide-react";

// Primary admin sections live inside the /admin page (tab query param).
const PRIMARY = [
  ["metrics", "Metrics", Gauge],
  ["create", "Create Contest", Plus],
  ["contests", "Manage Contests", ListChecks],
  ["review", "Review Queue", AlertTriangle],
  ["content", "Content Engine", Sparkles],
  ["audit", "Audit Log", ScrollText],
  ["users", "Users", Users],
  ["team", "Team", UserCog],
  ["4thave", "4th AVE", Landmark],
  ["affiliates", "Racing Teams", Handshake],
  ["payments", "Prize Funding", DollarSign],
  ["fulfillment", "Prize Fulfillment", Gift],
  ["regions", "Restricted Regions", Globe],
  ["sms", "SMS Reminders", MessageSquare],
];

// Standalone tool pages (their own routes).
const TOOLS = [
  { to: "/admin/ad-network", label: "Ad Network", Icon: Megaphone, badgeKey: "lowBudget", badgeAmber: true },
  { to: "/admin/advertisers", label: "Advertisers", Icon: Handshake, badgeKey: "advSubmissions", badgeAmber: true },
  { to: "/admin/sop-library", label: "SOP Library", Icon: BookCheck },
  { to: "/admin/debug-intelligence", label: "Debug Intel", Icon: Brain },
  { to: "/admin/race-control", label: "Race Control", Icon: Radio, accent: true, badgeKey: "raceControl" },
];

export const AdminSidebar = () => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [badges, setBadges] = useState({ raceControl: 0, lowBudget: 0, fulfillment: 0, advSubmissions: 0 });
  const onAdmin = pathname === "/admin";
  const activeTab = params.get("tab") || "metrics";

  useEffect(() => {
    const refresh = () => {
      api.get("/admin/race-control").then((r) => setBadges((b) => ({ ...b, raceControl: (r.data?.events || []).filter((e) => !e.ok).length }))).catch(() => {});
      api.get("/ads/admin/metrics").then((r) => setBadges((b) => ({ ...b, lowBudget: r.data?.low_budget_count || 0 }))).catch(() => {});
      api.get("/admin/prizes?status=ready_to_fulfill").then((r) => setBadges((b) => ({ ...b, fulfillment: r.data?.summary?.to_fulfill || 0 }))).catch(() => {});
      api.get("/adv/admin/counts").then((r) => setBadges((b) => ({ ...b, advSubmissions: r.data?.pending_review || 0 }))).catch(() => {});
    };
    refresh();
    window.addEventListener("adv-submissions-changed", refresh);
    return () => window.removeEventListener("adv-submissions-changed", refresh);
  }, [pathname]);

  const badge = (count, amber) => count > 0 && (
    <span className={`ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold ${amber ? "bg-amber-400 text-race-bg" : "bg-race-red text-race-bg"}`}>{count}</span>
  );
  const itemCls = (active, accent) =>
    `flex items-center gap-2 justify-start w-full whitespace-nowrap shrink-0 px-3 py-2 rounded-sm text-sm transition-colors ${
      active ? "bg-race-red text-race-bg font-semibold"
      : accent ? "text-race-red hover:bg-race-elevated" : "text-race-sub hover:bg-race-elevated hover:text-race-txt"}`;

  return (
    <nav data-testid="admin-sidebar"
      className="bg-race-surface border border-race-line h-auto w-full lg:w-56 shrink-0 flex lg:flex-col flex-nowrap overflow-x-auto lg:overflow-visible p-1 gap-1 lg:sticky lg:top-4 self-start rounded-md">
      {PRIMARY.map(([key, label, Icon]) => {
        const active = onAdmin && activeTab === key;
        return (
          <Link key={key} to={key === "metrics" ? "/admin" : `/admin?tab=${key}`} data-testid={`admin-tab-${key}`}
            className={itemCls(active, false)}>
            <Icon size={14} className="shrink-0" /> {label}
            {key === "fulfillment" && badge(badges.fulfillment, false)}
          </Link>
        );
      })}
      <div className="hidden lg:block h-px bg-race-line my-1 mx-1" />
      <div className="hidden lg:block px-3 pt-1 pb-0.5 overline text-race-muted">Tools</div>
      {TOOLS.map(({ to, label, Icon, badgeKey, badgeAmber, accent }) => {
        const active = pathname === to;
        const count = badgeKey ? badges[badgeKey] : 0;
        return (
          <Link key={to} to={to} data-testid={`admin-tool-${to.split("/").pop()}`} className={itemCls(active, accent)}>
            <Icon size={14} className="shrink-0" /> {label}
            {badge(count, badgeAmber)}
          </Link>
        );
      })}
    </nav>
  );
};

export const AdminShell = ({ children }) => (
  <div className="animate-slide-up px-4 py-6 max-w-[1400px] mx-auto">
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      <AdminSidebar />
      <div className="flex-1 min-w-0 w-full">{children}</div>
    </div>
  </div>
);

export default AdminSidebar;
